/**
 * SA-448 (spec 2026-09-22 §3.1): the map's grid constants and the layout
 * module's output contract. The output is renderer-agnostic on purpose — a
 * future canvas (wireframe decision 12, deferred) would consume it unchanged.
 */
export const MAP_GRID = {
  CARD_W: 156,
  CARD_H: 90,
  COL_GAP: 44,
  COL_PITCH: 200,
  X0: 118,
  PAD_RIGHT: 24,
  PAD_BOTTOM: 16,
  MIN_COLUMNS: 2,
  DEFAULT_COLUMNS: 5,
  TRACK: 14,
  ARC_LEVEL: 16,
  VTRACK: 12,
  VTRACK_MAX: 4,
  STUB_HEAD: 18,
  STUB_LINE: 12,
  ZONE_PAD: 8,
  CHANNEL_MIN: 40,
  PORT_INSET: 18,
  LABEL_H: 12,
  CHAR_W: 6.2,
  STUB_WRAP: 16,
  LANE_LABEL_W: 96,
  // SA-454: the lane label's own column. The left margin is reserved for it —
  // spec §3.4 already keeps every LINE out of the margins; these put the text
  // in the layout's model so no LABEL may enter it either.
  LANE_LABEL_X: 10,
  LANE_LABEL_TOP: 20,
  LANE_LABEL_LINE: 15,
  LANE_COUNT_GAP: 2,
  LIST_FLOOR_PX: 384,
} as const

export type EdgeKind = 'straight' | 'arc' | 'drop' | 'route'
export type StubKind = 'close' | 'notify' | 'self'

export interface Point { x: number; y: number }
export interface Rect { x: number; y: number; w: number; h: number }

/** A text anchor: `y` is the baseline, 4px below the line the label sits on. */
export interface LabelBox {
  text: string
  x: number
  y: number
  anchor: 'start' | 'middle' | 'end'
  w: number
  h: number
}
export interface LaneBox {
  id: string
  label: string
  labelLines: string[]
  count: number
  x: number
  y: number
  w: number
  h: number
  /** SA-454: the wrapped label lines and the "N steps" line as drawn boxes —
   * the renderer reads these instead of positioning the text itself. */
  labels: LabelBox[]
}
export interface CardBox { id: string; x: number; y: number; w: number; h: number; lane: string; row: number; col: number; globalRow: number }
export interface EdgePath {
  key: string
  from: string
  to: string
  exit: string
  kind: EdgeKind
  back: boolean
  /** The orthogonal polyline the path follows; `d` rounds its corners. */
  points: Point[]
  d: string
  label: LabelBox | null
}
export interface StubLine { exit: string; kind: StubKind; text: string }
export interface StubBlock {
  step: string
  x: number
  y: number
  arrowD: string
  lines: StubLine[]
  /** SA-454: each wrapped line carries the exit it belongs to, so a record
   * that took one of a card's stub exits lights only that line's text. */
  labels: (LabelBox & { exit: string })[]
}
export interface MapLayout {
  width: number
  height: number
  columns: number
  lanes: LaneBox[]
  cards: CardBox[]
  edges: EdgePath[]
  stubs: StubBlock[]
}

/** The rectangle a label occupies (spec §3.6): centred on its line, so the
 * box is the line ± LABEL_H / 2, and the line is 4px above the baseline. */
export function labelRect(l: LabelBox): Rect {
  const left = l.anchor === 'middle' ? l.x - l.w / 2 : l.anchor === 'start' ? l.x : l.x - l.w
  return { x: left, y: l.y - 4 - l.h / 2, w: l.w, h: l.h }
}
